import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const t = (s) => (s ? String(s).slice(0, 19).replace('T', ' ') + 'Z' : '—');

// Red banner shown on every admin view while any e-mail failure is unacknowledged
export const EmailFailureBanner = ({ data, onOpen }) => {
  if (!data || !data.open_count) return null;
  return (
    <div className="bg-red-600 text-white" data-testid="email-failure-banner" role="alert">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-white text-red-700 font-bold" data-testid="email-failure-badge">{data.open_total}</span>
        <span className="font-semibold">E-mail failure{data.open_total === 1 ? '' : 's'} need your acknowledgement — {data.open_count} group{data.open_count === 1 ? '' : 's'}: {data.open.slice(0, 3).map((r) => `${r.email_type} · ${r.label || r.outcome}`).join(' · ')}{data.open_count > 3 ? ' …' : ''}</span>
        <Button size="sm" variant="secondary" className="rounded-full ml-auto" onClick={onOpen} data-testid="email-failure-open-alerts">Open Alerts</Button>
      </div>
    </div>
  );
};

const FailureRow = ({ r, onAck, acked }) => (
  <tr className={`border-t border-border/50 ${acked ? 'text-muted-foreground' : ''}`} data-testid={`email-failure-row-${r.id}`}>
    <td className="py-1 pr-3 font-mono text-xs whitespace-nowrap">{t(r.last_at)}{r.count > 1 && <div className="text-[10px]">first {t(r.first_at)}</div>}</td>
    <td className="py-1 pr-3 font-mono text-xs">{r.email_type}</td>
    <td className="py-1 pr-3 text-xs"><span className={`px-2 py-0.5 rounded-full ${acked ? 'bg-slate-100' : 'bg-red-100 text-red-800'}`}>{r.outcome}</span><div className="text-[11px] mt-0.5">{r.label}{r.last_detail ? ` — ${r.last_detail}` : ''}</div></td>
    <td className="py-1 pr-3 font-mono text-xs">{(r.accounts || []).filter(Boolean).join(', ') || '—'}</td>
    <td className="py-1 pr-3 text-xs font-bold text-center" data-testid={`email-failure-count-${r.id}`}>×{r.count}</td>
    <td className="py-1 text-xs">
      {acked ? <span className="font-mono">ack {t(r.acknowledged_at)} by {r.acknowledged_by}</span>
        : <Button size="sm" variant="outline" className="rounded-full h-7" onClick={() => onAck(r.id)} data-testid={`email-failure-ack-${r.id}`}>Acknowledge</Button>}
    </td>
  </tr>
);

export const EmailFailuresView = ({ data, onAck }) => {
  if (!data) return <p className="text-sm text-muted-foreground" data-testid="email-failures-loading">Loading e-mail failures…</p>;
  const Table = ({ rows, acked }) => (
    <table className="w-full text-left">
      <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground"><th className="pr-3">Time</th><th className="pr-3">E-mail type</th><th className="pr-3">Outcome</th><th className="pr-3">Account</th><th className="pr-3 text-center">Count</th><th></th></tr></thead>
      <tbody>{rows.map((r) => <FailureRow key={r.id} r={r} onAck={onAck} acked={acked} />)}</tbody>
    </table>
  );
  return (
    <div className="space-y-6" data-testid="email-failures-view">
      <div className={`rounded-2xl border p-5 ${data.open_count ? 'border-red-300 bg-red-50/60' : 'border-green-200 bg-green-50/60'}`}>
        <h3 className="font-bold text-lg mb-1">E-mail failures — {data.open_count ? `${data.open_total} open in ${data.open_count} group${data.open_count === 1 ? '' : 's'}` : 'none open'}</h3>
        <p className="text-xs text-muted-foreground mb-3">Postmark refusals, bounces and spam complaints, accepted-but-undelivered after 2 h, failed daily check. Repeats are grouped; a row stays until you acknowledge it. The same episode is e-mailed once to the alert address. No addresses or message content are stored here.</p>
        {data.open_count ? <Table rows={data.open} acked={false} /> : <p className="text-sm" data-testid="email-failures-none">Nothing to acknowledge.</p>}
      </div>
      {data.acknowledged?.length > 0 && (
        <div className="rounded-2xl border border-border/60 p-5">
          <h4 className="font-semibold mb-2 text-sm">Recently acknowledged</h4>
          <Table rows={data.acknowledged} acked />
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">Compares: {data.compares}</p>
    </div>
  );
};

export const PostmarkUsage = ({ usage }) => {
  if (!usage) return null;
  const pct = usage.percent;
  const tone = pct == null ? 'text-muted-foreground' : pct >= 100 ? 'text-red-700 font-bold' : pct >= 80 ? 'text-amber-700 font-bold' : 'text-green-700';
  return (
    <p className={`text-xs mt-1 ${tone}`} data-testid="postmark-usage">
      Usage {usage.month}: {usage.error ? `unavailable (${usage.error})` : `${usage.sent_this_month} sent`}
      {usage.limit ? ` of ${usage.limit} (${pct}%)${pct >= 80 ? ' — WARNING: at or above 80% of the plan limit' : ''}` : ' — plan limit not set (POSTMARK_MONTHLY_LIMIT)'}
      <span className="block text-[10px] text-muted-foreground font-normal">{usage.scope}</span>
    </p>
  );
};

// MessageID lookup: Postmark's Status + events for one message, addresses removed server-side
export const MessageLookup = ({ token, initialId = '', compact = false }) => {
  const [id, setId] = useState(initialId);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (mid) => {
    const target = (mid || id || '').trim();
    if (!target) return;
    setBusy(true); setRes(null);
    try {
      const r = await axios.get(`${API_URL}/api/admin/email/outbound/${encodeURIComponent(target)}`, { headers: { Authorization: `Bearer ${token}` } });
      setRes(r.data);
    } catch (e) {
      setRes({ error: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  const pm = res?.postmark;
  return (
    <div className={compact ? 'inline-block' : 'rounded-lg border border-border/60 p-3 mt-3'} data-testid="message-lookup">
      {!compact && <p className="text-sm font-semibold mb-1">Look up a Postmark MessageID</p>}
      <div className="flex gap-2 items-center flex-wrap">
        {!compact && <input value={id} onChange={(e) => setId(e.target.value)} placeholder="MessageID from a reset-attempt row or Postmark Activity" className="border rounded-full px-3 py-1 text-sm flex-1 min-w-[260px]" data-testid="message-lookup-input" />}
        <Button size="sm" variant="outline" className="rounded-full h-7" disabled={busy} onClick={() => run(initialId)} data-testid="message-lookup-btn">{busy ? 'Asking Postmark…' : compact ? 'Postmark status' : 'Look up'}</Button>
      </div>
      {res && (
        <div className="text-xs mt-2 font-mono bg-white/70 rounded p-2 break-all" data-testid="message-lookup-result">
          {res.error && <p className="text-red-700">{res.error}</p>}
          {res.local_record && <p>local: {res.local_record.ok ? 'postmark_accepted' : res.local_record.error?.kind} · {res.local_record.tag || res.local_record.subject} · account {res.local_record.account || '—'} · submitted {t(res.local_record.attempted_at)}{res.local_record.delivered_at ? ` · delivered ${t(res.local_record.delivered_at)}` : ''}{res.local_record.bounce ? ` · bounce ${res.local_record.bounce.type}` : ''}</p>}
          {res.local_record === null && <p>local: no row with this MessageID in this environment</p>}
          {pm && pm.ok === false && <p className="text-red-700">postmark: {pm.error?.kind || 'error'} — {pm.error?.message}</p>}
          {pm && pm.ok !== false && (
            <>
              <p>postmark: Status {pm.Status || '—'} · stream {pm.MessageStream || '—'} · tag {pm.Tag || '—'} · received {t(pm.ReceivedAt)}</p>
              <ul className="mt-1 list-disc pl-4">{(pm.MessageEvents || []).map((e, i) => <li key={i}>{e.Type} · {t(e.ReceivedAt)}{e.Details?.DeliveryMessage ? ` · ${e.Details.DeliveryMessage}` : ''}{e.Details?.DestinationServer ? ` · ${e.Details.DestinationServer}` : ''}{e.Details?.BounceID ? ` · bounce ${e.Details.BounceID}` : ''}</li>)}</ul>
              {!(pm.MessageEvents || []).length && <p>no events yet (Delivery/Bounce events require the Postmark webhook — see Startup Checks → Postmark)</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
};
